import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Receipt, CheckCircle, ArrowRight, PaperPlaneTilt, Hash } from "@phosphor-icons/react";
import { toast } from "sonner";

/* Source → invoice linkage. One place to know the query param / filter used per entity. */
export const INVOICE_SOURCES = {
  job: { param: "job_id", label: "job" },
  emergency_job: { param: "emergency_id", filter: "emergency_job_id", label: "emergency job" },
  project: { param: "project_id", label: "project" },
};

export const raiseInvoicePath = (sourceType, sourceId) => `/invoices/new?${INVOICE_SOURCES[sourceType].param}=${sourceId}`;

/* Sonner toast shown when something is marked completed.
   opts.onUndo   → adds an "Undo" button (10 s window) that reverts the status.
   opts.invoice  → (default true) adds the "Raise invoice" shortcut. */
export const completionToast = (navigate, sourceType, sourceId, label = "Marked as completed", opts = {}) => {
  const { onUndo, invoice = true } = opts;
  const raise = { label: "Raise invoice", onClick: () => navigate(raiseInvoicePath(sourceType, sourceId)) };
  const undo = onUndo ? { label: "Undo", onClick: () => onUndo() } : undefined;
  return toast.success(label, {
    description: invoice ? "Ready to bill the client?" : (undo ? "Mis-click? You can undo for 10 seconds." : undefined),
    duration: undo ? 10000 : 8000,
    action: invoice ? raise : undo,
    cancel: invoice ? undo : undefined,
  });
};

const STATUS_CLS = {
  draft: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  sent: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  paid: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  overdue: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  cancelled: "bg-slate-100 text-slate-500 line-through",
};
const money = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function RaiseInvoiceButton({ sourceType, sourceId, size = "sm", className = "", testid = "raise-invoice-btn", disabled = false }) {
  const navigate = useNavigate();
  return (
    <Button variant="outline" size={size} disabled={disabled} title={disabled ? "Enter the client PO/WO number (or waive it) first" : undefined} onClick={() => navigate(raiseInvoicePath(sourceType, sourceId))} data-testid={testid}
      className={`bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800/50 ${className}`}>
      <Receipt size={14} className="mr-1" /> Raise Invoice
    </Button>
  );
}

export function useLinkedInvoices(sourceType, sourceId, enabled = true) {
  const [invoices, setInvoices] = useState([]);
  const filter = INVOICE_SOURCES[sourceType].filter || INVOICE_SOURCES[sourceType].param;
  const reload = useCallback(() => {
    if (!enabled || !sourceId) return;
    API.get("/invoices", { params: { [filter]: sourceId, limit: 100 } }).then(({ data }) => setInvoices(data.invoices || [])).catch(() => {});
  }, [filter, sourceId, enabled]);
  useEffect(() => { reload(); }, [reload]);
  return { invoices, reload };
}

/* Inline "Client PO / WO #" capture — saved against the job so it lands on the invoice. */
export function ClientPoField({ sourceType, sourceId, value, onSaved, compact = false, required = false }) {
  const [po, setPo] = useState(value || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setPo(value || ""); }, [value]);
  const base = sourceType === "emergency_job" ? "/emergency-jobs" : "/jobs";
  const save = async () => {
    if ((po || "").trim() === (value || "").trim()) return;
    setSaving(true);
    try {
      const { data } = await API.put(`${base}/${sourceId}/client-po`, { client_po_number: po.trim() });
      toast.success(data.client_po_number ? `Client PO/WO ${data.client_po_number} saved — it'll be on the invoice` : "Client PO/WO cleared");
      onSaved?.(data.client_po_number, data);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save PO/WO number"); }
    finally { setSaving(false); }
  };
  return (
    <div className={`flex items-center gap-1.5 ${compact ? "" : "shrink-0"}`} data-testid="client-po-field">
      <input value={po} onChange={(e) => setPo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} placeholder={required ? "Client PO / WO # (required)" : "Client PO / WO #"} maxLength={60}
        className={`h-8 w-44 rounded-md border bg-background px-2 text-xs font-mono ${required && !(po || "").trim() ? "border-amber-400" : "border-input"}`} data-testid="client-po-input" />
      <Button size="sm" variant="outline" className="h-8 text-xs" disabled={saving || (po || "").trim() === (value || "").trim()} onClick={save} data-testid="client-po-save-btn">{saving ? "Saving…" : "Save"}</Button>
    </div>
  );
}

/* "No PO/WO issued" override for PO-required clients — waives the rule on this job only (reason optional, logged on the timeline). */
function PoWaiverButton({ sourceType, sourceId, waived, onSaved }) {
  const [busy, setBusy] = useState(false);
  const base = sourceType === "emergency_job" ? "/emergency-jobs" : "/jobs";
  const toggle = async () => {
    let reason = "";
    if (!waived) {
      reason = window.prompt("Invoice this job without a client PO/WO number?\nOptional reason (e.g. \"Client doesn't issue PO/WOs\"):", "Client doesn't issue PO/WOs");
      if (reason === null) return;
    }
    setBusy(true);
    try {
      const { data } = await API.put(`${base}/${sourceId}/client-po`, { po_waived: !waived, po_waiver_reason: reason });
      toast.success(data.po_waived ? "PO/WO waived for this job — you can raise the invoice" : "Waiver removed — a PO/WO number is required again");
      onSaved?.(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update waiver"); }
    finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant={waived ? "ghost" : "outline"} className={`h-8 text-xs ${waived ? "text-muted-foreground" : "border-amber-400 text-amber-800 dark:text-amber-300 hover:bg-amber-100/60"}`} disabled={busy} onClick={toggle} data-testid="po-waiver-btn">
      {waived ? "Undo waiver" : "No PO/WO issued — invoice anyway"}
    </Button>
  );
}

/* Green "Completed — raise the client invoice" banner. Hidden once a live (non-cancelled) invoice exists.
   Turns amber when the bill-to client requires a PO/WO and none is saved (Raise Invoice disabled until entered or waived). */
export function InvoiceCompletionBanner({ sourceType, sourceId, reference, completed, invoices, clientPoNumber, onPoSaved, poRequired = false, poRequiredBy = "", poWaived = false, poWaiverReason = "", onPoWaived }) {
  const live = (invoices || []).filter((i) => i.status !== "cancelled");
  if (!completed || live.length > 0) return null;
  const blocked = poRequired && !(clientPoNumber || "").trim() && !poWaived;
  const tone = blocked ? "border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30" : "border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30";
  return (
    <div className={`mb-5 border rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${tone}`} data-testid="invoice-completion-banner" data-po-blocked={blocked ? "true" : "false"}>
      {blocked ? <Hash size={26} weight="bold" className="text-amber-500 shrink-0" /> : <CheckCircle size={26} weight="fill" className="text-emerald-500 shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold ${blocked ? "text-amber-900 dark:text-amber-200" : "text-emerald-900 dark:text-emerald-200"}`} data-testid="invoice-completion-title">
          {reference ? `${reference} is complete` : "Work complete"} — {blocked ? `PO/WO required by ${poRequiredBy || "the client"}` : "raise the client invoice"}
        </p>
        <p className={`text-[11px] ${blocked ? "text-amber-800/80 dark:text-amber-300/80" : "text-emerald-800/80 dark:text-emerald-300/80"}`}>
          {blocked
            ? `${poRequiredBy || "This client"} won't be invoiced without a PO/WO number. Enter it here, or use the override if this client doesn't issue them.`
            : poWaived
              ? `PO/WO waived for this job${poWaiverReason ? ` — ${poWaiverReason}` : ""}. Client, property, contacts and line items are pulled across ready to edit.`
              : "Add the client's (or sub-client's) PO / WO number first so it prints on the invoice. Client, property, contacts and line items are pulled across ready to edit."}
        </p>
      </div>
      {onPoSaved && <ClientPoField sourceType={sourceType} sourceId={sourceId} value={clientPoNumber} onSaved={onPoSaved} required={poRequired && !poWaived} />}
      {poRequired && onPoWaived && !(clientPoNumber || "").trim() && <PoWaiverButton sourceType={sourceType} sourceId={sourceId} waived={poWaived} onSaved={onPoWaived} />}
      <RaiseInvoiceButton sourceType={sourceType} sourceId={sourceId} disabled={blocked} className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600 dark:bg-emerald-600 dark:text-white disabled:opacity-50" testid="banner-raise-invoice-btn" />
    </div>
  );
}

/* Compact linked-invoice list for Emergency / Project pages (Jobs keep their richer existing block). */
export function LinkedInvoicesCard({ sourceType, sourceId, invoices }) {
  const total = invoices.filter((i) => i.status !== "cancelled").reduce((s, i) => s + Number(i.total_inc_vat || i.grand_total || 0), 0);
  return (
    <div className="border border-border rounded-lg p-4" data-testid="linked-invoices-card">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Receipt size={12} /> Invoices ({invoices.length})</h3>
          {invoices.length > 0 && <p className="text-[10px] text-muted-foreground mt-0.5">Billed {money(total)}</p>}
        </div>
        <RaiseInvoiceButton sourceType={sourceType} sourceId={sourceId} className="h-7 text-xs" testid="linked-invoices-raise-btn" />
      </div>
      {invoices.length === 0 ? <p className="text-xs text-muted-foreground italic text-center py-2">No invoices raised yet.</p> : (
        <ul className="divide-y divide-border">
          {invoices.map((inv) => (
            <li key={inv.id}>
              <Link to={`/invoices/${inv.id}`} className="flex items-center gap-2 py-2 hover:bg-muted/40 -mx-1 px-1 rounded" data-testid={`linked-invoice-${inv.id}`}>
                <span className="text-[10px] font-mono text-muted-foreground">{inv.reference}</span>
                <span className="text-xs flex-1 truncate">{inv.title}</span>
                {inv.portal_sent_at && <span title="In client portal" className="text-blue-500"><PaperPlaneTilt size={12} /></span>}
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold uppercase ${STATUS_CLS[inv.status] || ""}`}>{inv.status}</span>
                <span className="text-xs font-semibold">{money(inv.total_inc_vat || inv.grand_total)}</span>
                <ArrowRight size={12} className="text-muted-foreground" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

